import React from "react";
import { useTranslation } from "react-i18next";

import { IOrderDetail } from "../../../apis/useGetOrderDetail/types";
import CheckIcon from "../../../assets/images/CheckIcon";
import ChevronDownIcon from "../../../assets/images/ChevronDownIcon";
import ChevronRightIcon from "../../../assets/images/ChevronRightIcon";
import ChevronUpIcon from "../../../assets/images/ChevronUpIcon";
import {
	displayCentsWithDollarSign,
	formatToDisplayTime,
} from "../../../common/helpers/stringUtils";
import { GST_REGEX, SERVICE_CHARGE_REGEX, findTaxEntry } from "../../../common/helpers/taxHelper";
import Condition from "../../../components/Conditional/Condition";
import "../Order.scss";
import OrderDetailsCard from "../OrderDetailsCard/OrderDetailsCard";
import "../Order_flint.scss";
import "../Order_kokoro.scss";
import "../Order_pdd.scss";
import "../Order_shabuDays.scss";
import "../Order_wang.scss";
import PaymentReceiptCard from "../PaymentReceiptCard/PaymentReceiptCard";
import { downloadreceipt } from "../downloadreceipt";

interface OrderLayoutDefaultProps {
	orderDetail: IOrderDetail;
	showPaymentReceipt: boolean;
	onTogglePaymentReceipt: () => void;
	onNewOrder: () => void;
	totalAmount: number;
	orderType: string;
	gstRate: number;
	orderTimestamp: string;
}

const OrderLayoutDefault: React.FC<OrderLayoutDefaultProps> = ({
	orderDetail,
	showPaymentReceipt,
	onTogglePaymentReceipt,
	onNewOrder,
	totalAmount,
	orderType,
	gstRate,
	orderTimestamp,
}) => {
	const { t } = useTranslation();

	const serviceChargeTax = findTaxEntry(orderDetail.order_items.tax, SERVICE_CHARGE_REGEX);
	const gstTax = findTaxEntry(orderDetail.order_items.tax, GST_REGEX);

	return (
		<div className="OrderContainer">
			<div id="ReceiptCapture" className="ReceiptCapture">
				<div className="IconSection">
					<CheckIcon />
				</div>

				<h1 className="MainTitle">{t("orderConfirmation.title")}</h1>
				<p className="Timestamp">{orderTimestamp}</p>
				<div className="TotalOrderSection">
					<div>
						<div className="TotalLabel">{t("orderConfirmation.totalOrder")}</div>
						<Condition check={!!gstTax}>
							<div className="GstInfo">{gstTax?.name}</div>
						</Condition>
						<Condition check={!!serviceChargeTax}>
							<div className="GstInfo ServiceChargeInfo">{serviceChargeTax?.name}</div>
						</Condition>
					</div>
					<p className="TotalAmount">{displayCentsWithDollarSign(totalAmount)}</p>
				</div>

				<div className="OrderNumberSection">
					<p className="OrderNumberLabel">{t("orderConfirmation.orderNumber")}</p>
					<p className="OrderNumber TabularNums">{orderDetail.order_reference}</p>
					<p className="OrderType">{orderType}</p>
					<Condition check={!!orderDetail.table}>
						<div className="TableNumber">
							<p className="TableNumberLabel">{t("orderConfirmation.tableNumber")}</p>
							<h4 className="TableNumberValue">{orderDetail.table}</h4>
						</div>
					</Condition>
					<p className="PickupInstruction">{t("orderConfirmation.pickupInstruction")}</p>
				</div>

				<button className="DetailsLink" onClick={onTogglePaymentReceipt}>
					<span className="DetailsLinkLabel">{t("orderConfirmation.viewreceipt")}</span>
					<Condition check={showPaymentReceipt}>
						<ChevronUpIcon />
					</Condition>
					<Condition check={!showPaymentReceipt}>
						<ChevronDownIcon />
					</Condition>
				</button>

				<Condition check={showPaymentReceipt}>
					<div className="ReceiptBg" id="ReceiptCard">
						<PaymentReceiptCard
							timestamp={formatToDisplayTime(orderDetail.created_at)}
							companyName={orderDetail.payment_receipt_detail.gst_company_name}
							uenNumber={orderDetail.payment_receipt_detail.uen_no}
							gstNumber={orderDetail.payment_receipt_detail.gst_no}
							transactionNumber={orderDetail.order_reference}
							paymentReference={orderDetail.payment_receipt_detail.payment_reference ?? ""}
							totalAmount={displayCentsWithDollarSign(totalAmount)}
							paymentMethod={orderDetail.payment_receipt_detail.payment_method}
							gstAmount={displayCentsWithDollarSign(orderDetail.order_items.tax?.[0]?.amount ?? 0)}
						/>
						<span className="ReceiptTitle">{t("orderConfirmation.receipt")}</span>
						<OrderDetailsCard
							orderItems={orderDetail.order_items.item_info}
							subtotal_value={orderDetail.order_items.orders_figures.subtotal_value}
							net_value={orderDetail.order_items.orders_figures.net_value}
							takeawayFee={orderDetail.order_items.order_level_takeaway_fee}
							tax={orderDetail.order_items.tax}
							isTakeaway={orderDetail.order_items.is_takeaway}
							discounts={orderDetail.order_items.discounts || []}
						/>
					</div>
				</Condition>
			</div>

			<button
				className="DownloadReceipt"
				onClick={downloadreceipt(showPaymentReceipt ? undefined : onTogglePaymentReceipt)}
			>
				{t("orderConfirmation.downloadreceipt")}
				<ChevronRightIcon />
			</button>

			<button className="InvoiceButton">
				<span>{t("orderConfirmation.viewInvoice")}</span>
				<ChevronRightIcon />
			</button>

			<div className="HelpText">
				<p>{t("orderConfirmation.helpTitle")}</p>
				<p>{t("orderConfirmation.helpDescription")}</p>
			</div>

			<button className="PrimaryButton" onClick={onNewOrder}>
				{t("orderConfirmation.newOrder")}
			</button>
		</div>
	);
};

export default OrderLayoutDefault;
